import { Action, Reducer } from 'redux';
import { AppThunkAction } from '..';
import { WeightAlertGroupRecord } from '../../Interfaces/Alerts/AlertRecord';
import { getRequestOptions, postRequestOptions } from '../../utils/utils';
import { AlertCounts } from '../../Interfaces/Alerts/AlertCounts';
import { ApiEnums } from '../../Interfaces/ApiEnums';

export enum AlertActionTypes {
  RESET_STATE = 'RESET_STATE',
  SET_REFRESH_ALERTS = 'SET_REFRESH_ALERTS',
  CLEAR_ALL_ALERTS = 'CLEAR_ALL_ALERTS',
  REQUEST_ALL_ACTIVE_ALERTS = 'REQUEST_ALL_ACTIVE_ALERTS',
  RECEIVE_ALL_ACTIVE_ALERTS = 'RECEIVE_ALL_ACTIVE_ALERTS',
  RECEIVE_ALERT_COUNTS = 'RECEIVE_ALERT_COUNTS',
  SEND_COMMAND_RESPONSE = 'SEND_COMMAND_RESPONSE',
  SENT_COMMAND_RESPONSE = 'SENT_COMMAND_RESPONSE',
  UNAUTHORIZED_REQUEST = 'UNAUTHORIZED_REQUEST',
  SET_START_DATE_TIME_FILTER = 'SET_START_DATE_TIME_FILTER',
  SET_END_DATE_TIME_FILTER = 'SET_END_DATE_TIME_FILTER',
  USE_CURRENT_DATE_TIME = 'USE_CURRENT_DATE_TIME',
  REQUEST_ALL_NEW_ALERTS = 'REQUEST_ALL_NEW_ALERTS',
  RECEIVE_ALL_NEW_ALERTS = 'RECEIVE_ALL_NEW_ALERTS',
  REQUEST_ALL_ALERTS_BY_COMMAND_STATUS = 'REQUEST_ALL_ALERTS_BY_COMMAND_STATUS',
  RECEIVE_ALL_ALERTS_BY_COMMAND_STATUS = 'RECEIVE_ALL_ALERTS_BY_COMMAND_STATUS',
}
// -----------------
// STATE - This defines the type of data maintained in the Redux store.
export interface AlertsState {
  allAlerts: WeightAlertGroupRecord[];
  isLoadingAllAlerts: boolean;
  openAlertsCount: number;
  inProgressAlertsCount: number;
  closedAlertsCount: number;
  shouldRefreshAlerts: boolean;
  unauthorizedRequest: boolean;
  startDateTimeFilter: Date;
  endDateTimeFilter: Date;
  useCurrentDateTimeFilter: boolean;
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.
export interface ResetStateAction {
  type: AlertActionTypes.RESET_STATE;
}

export interface RefreshAlertsAction {
  type: AlertActionTypes.SET_REFRESH_ALERTS;
  value: boolean;
}

export interface ClearAllAlertsAction {
  type: AlertActionTypes.CLEAR_ALL_ALERTS;
}

export interface RequestAllActiveAlertsAction {
  type: AlertActionTypes.REQUEST_ALL_ACTIVE_ALERTS;
}

export interface ReceiveAllActiveAlertsAction {
  type: AlertActionTypes.RECEIVE_ALL_ACTIVE_ALERTS;
  allAlerts: WeightAlertGroupRecord[];
}

export interface ReceiveAlertCountsAction {
  type: AlertActionTypes.RECEIVE_ALERT_COUNTS;
  alertCounts: AlertCounts;
}

export interface SendCommandResponseAction {
  type: AlertActionTypes.SEND_COMMAND_RESPONSE;
  commandName: string;
}

export interface SentCommandResponseAction {
  type: AlertActionTypes.SENT_COMMAND_RESPONSE;
  commandName: string;
}

export interface UnauthorizedRequestAction {
  type: AlertActionTypes.UNAUTHORIZED_REQUEST;
}

export interface SetStartDateTimeFilterAction {
  type: AlertActionTypes.SET_START_DATE_TIME_FILTER;
  date: Date;
}

export interface SetEndDateTimeFilterAction {
  type: AlertActionTypes.SET_END_DATE_TIME_FILTER;
  date: Date;
}

export interface UseCurrentDateTimeAction {
  type: AlertActionTypes.USE_CURRENT_DATE_TIME;
  value: boolean;
}

export interface RequestAllNewAlertsAction {
  type: AlertActionTypes.REQUEST_ALL_NEW_ALERTS;
}

export interface ReceiveAllNewAlertsAction {
  type: AlertActionTypes.RECEIVE_ALL_NEW_ALERTS;
  allAlerts: WeightAlertGroupRecord[];
}

export interface RequestAllAlertsByCommandStatusAction {
  type: AlertActionTypes.REQUEST_ALL_ALERTS_BY_COMMAND_STATUS;
}

export interface ReceiveAllAlertsByCommandStatusAction {
  type: AlertActionTypes.RECEIVE_ALL_ALERTS_BY_COMMAND_STATUS;
  allAlerts: WeightAlertGroupRecord[];
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
export type KnownAction =
  | ResetStateAction
  | RefreshAlertsAction
  | ClearAllAlertsAction
  | RequestAllActiveAlertsAction
  | ReceiveAllActiveAlertsAction
  | ReceiveAlertCountsAction
  | SendCommandResponseAction
  | SentCommandResponseAction
  | UnauthorizedRequestAction
  | SetStartDateTimeFilterAction
  | SetEndDateTimeFilterAction
  | UseCurrentDateTimeAction
  | RequestAllNewAlertsAction
  | ReceiveAllNewAlertsAction
  | RequestAllAlertsByCommandStatusAction
  | ReceiveAllAlertsByCommandStatusAction;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).
const isResponseOk = (response: Response, dispatch: any): boolean => {
  if (response.ok) return true;
  if (response.status === 401) {
    dispatch({
      type: AlertActionTypes.UNAUTHORIZED_REQUEST,
    });
  }
  return false;
};

const getEndDateTimeFilter = (
  useCurrentDateTimeFilter: boolean,
  endDateTimeFilter: Date
): string => {
  return useCurrentDateTimeFilter
    ? new Date().toISOString()
    : endDateTimeFilter.toISOString();
};

const getDefaultStartDateTime = (): Date => {
  return new Date(Date.now() - 30 * 24 * 60 * 60 * 1000); // 30 days ago
};

const getDefaultEndDateTime = (): Date => {
  return new Date(Date.now());
};

export const actionCreators = {
  resetState: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
    console.log(`Dispatching ${AlertActionTypes.RESET_STATE}`);
    dispatch({
      type: AlertActionTypes.RESET_STATE,
    });
  },

  setRefreshAlertsRequested:
    (refresh: boolean): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      console.log(
        `Dispatching ${AlertActionTypes.SET_REFRESH_ALERTS}`,
        refresh
      );
      dispatch({
        type: AlertActionTypes.SET_REFRESH_ALERTS,
        value: refresh,
      });
    },

  clearAllAlerts:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      console.log(`Dispatching ${AlertActionTypes.CLEAR_ALL_ALERTS}`);
      dispatch({
        type: AlertActionTypes.CLEAR_ALL_ALERTS,
      });
    },

  fetchAllAlerts:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const endDateTime: string = getEndDateTimeFilter(
        appState.alertsReducer.useCurrentDateTimeFilter,
        appState.alertsReducer.endDateTimeFilter
      );

      console.log(
        `Dispatching ${AlertActionTypes.REQUEST_ALL_ACTIVE_ALERTS}\n`,
        `start date=${appState.alertsReducer.startDateTimeFilter.toISOString()}\n`,
        `end date=${endDateTime}`
      );
      dispatch({
        type: AlertActionTypes.REQUEST_ALL_ACTIVE_ALERTS,
      });

      const response = await fetch(
        `alertrecords/getalerts/${appState.alertsReducer.startDateTimeFilter.toISOString()}/${endDateTime}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as WeightAlertGroupRecord[];

      console.log(`Dispatching ${AlertActionTypes.RECEIVE_ALL_ACTIVE_ALERTS}`);

      dispatch({
        type: AlertActionTypes.RECEIVE_ALL_ACTIVE_ALERTS,
        allAlerts: result,
      });
    },

  fetchAlertCounts:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      console.log(`Fetching alerts count`);

      const endDateTime: string = getEndDateTimeFilter(
        appState.alertsReducer.useCurrentDateTimeFilter,
        appState.alertsReducer.endDateTimeFilter
      );

      const response = await fetch(
        `alertrecords/GetAlertCounts/${appState.alertsReducer.startDateTimeFilter.toISOString()}/${endDateTime}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result: AlertCounts = await response.json().catch(error => {
        console.error(error);
        return;
      });

      dispatch({
        type: AlertActionTypes.RECEIVE_ALERT_COUNTS,
        alertCounts: result,
      });
      console.log(`Dispatching ${AlertActionTypes.RECEIVE_ALERT_COUNTS}`);
    },

  fetchAllNewAlerts:
    (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const endDateTime: string = getEndDateTimeFilter(
        appState.alertsReducer.useCurrentDateTimeFilter,
        appState.alertsReducer.endDateTimeFilter
      );

      console.log(
        `Dispatching ${AlertActionTypes.REQUEST_ALL_NEW_ALERTS}\n`,
        `start date=${appState.alertsReducer.startDateTimeFilter.toISOString()}\n`,
        `end date=${endDateTime}`
      );
      dispatch({
        type: AlertActionTypes.REQUEST_ALL_NEW_ALERTS,
      });

      const response = await fetch(
        `alertrecords/getallnewalerts/${appState.alertsReducer.startDateTimeFilter.toISOString()}/${endDateTime}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as WeightAlertGroupRecord[];

      console.log(`Dispatching ${AlertActionTypes.RECEIVE_ALL_NEW_ALERTS}`);

      dispatch({
        type: AlertActionTypes.RECEIVE_ALL_NEW_ALERTS,
        allAlerts: result,
      });
    },

  fetchAllAlertsByStatus:
    (commandStatus: ApiEnums.CommandStatus): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      const endDateTime: string = getEndDateTimeFilter(
        appState.alertsReducer.useCurrentDateTimeFilter,
        appState.alertsReducer.endDateTimeFilter
      );

      console.log(
        `Dispatching ${AlertActionTypes.REQUEST_ALL_ALERTS_BY_COMMAND_STATUS}\n`,
        `start date=${appState.alertsReducer.startDateTimeFilter.toISOString()}\n`,
        `end date=${endDateTime}`
      );
      dispatch({
        type: AlertActionTypes.REQUEST_ALL_ALERTS_BY_COMMAND_STATUS,
      });

      const response = await fetch(
        `alertrecords/getallalertsbycommandstatus/${appState.alertsReducer.startDateTimeFilter.toISOString()}/${endDateTime}/${commandStatus}`,
        getRequestOptions(appState.userSessionReducer.sessionInfo?.Token)
      ).catch(error => {
        console.error(error);
        return Promise.reject(error);
      });

      if (!isResponseOk(response, dispatch)) return;

      const result = (await response.json()) as WeightAlertGroupRecord[];

      console.log(
        `Dispatching ${AlertActionTypes.RECEIVE_ALL_ALERTS_BY_COMMAND_STATUS}`
      );

      dispatch({
        type: AlertActionTypes.RECEIVE_ALL_ALERTS_BY_COMMAND_STATUS,
        allAlerts: result,
      });
    },

  resetStartDateTimeFilter:
    (): AppThunkAction<KnownAction> => (dispatch, getState) => {
      console.log(`Resetting start date/time filter`);
      dispatch({
        type: AlertActionTypes.SET_START_DATE_TIME_FILTER,
        date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
      });
    },

  setStartDateTimeFilter:
    (date: Date): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Setting start date/time filter: ${date}`);
      dispatch({
        type: AlertActionTypes.SET_START_DATE_TIME_FILTER,
        date: date,
      });
    },

  resetEndDateTimeFilter:
    (): AppThunkAction<KnownAction> => (dispatch, getState) => {
      console.log(`Resetting end date/time filter`);
      dispatch({
        type: AlertActionTypes.SET_END_DATE_TIME_FILTER,
        date: getDefaultEndDateTime(),
      });
    },

  setEndDateTimeFilter:
    (date: Date): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      console.log(`Setting end date/time filter: ${date}`);
      dispatch({
        type: AlertActionTypes.SET_END_DATE_TIME_FILTER,
        date: date,
      });
    },

  UseCurrentDateTime:
    (value: boolean): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
      dispatch({
        type: AlertActionTypes.USE_CURRENT_DATE_TIME,
        value: value,
      });
    },

  sendCommandResponse:
    (commandName: string, commandResponse: any): AppThunkAction<KnownAction> =>
    async (dispatch, getState) => {
      const appState = getState();
      if (!appState) return;

      dispatch({
        type: AlertActionTypes.SEND_COMMAND_RESPONSE,
        commandName: commandName,
      });

      console.log(
        `Dispatching ${commandName} command response`,
        commandResponse
      );

      const response = await fetch(
        `commandresponse/${commandName}`,
        postRequestOptions(
          appState.userSessionReducer.sessionInfo?.Token,
          commandResponse
        )
      );

      if (!isResponseOk(response, dispatch)) return;

      dispatch({
        type: AlertActionTypes.SENT_COMMAND_RESPONSE,
        commandName: commandName,
      });
      console.log(
        `Dispatched ${commandName} command response`,
        commandResponse
      );
    },
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.
export const unloadedState: AlertsState = {
  allAlerts: [],
  isLoadingAllAlerts: false,
  openAlertsCount: 0,
  inProgressAlertsCount: 0,
  closedAlertsCount: 0,
  shouldRefreshAlerts: false,
  unauthorizedRequest: false,
  startDateTimeFilter: getDefaultStartDateTime(),
  endDateTimeFilter: getDefaultEndDateTime(),
  useCurrentDateTimeFilter: true,
};

export const reducer: Reducer<AlertsState> = (
  state: AlertsState | undefined,
  incomingAction: Action
): AlertsState => {
  if (state === undefined) {
    return unloadedState;
  }

  const action = incomingAction as KnownAction;
  switch (action.type) {
    case AlertActionTypes.RESET_STATE:
      return unloadedState;
    case AlertActionTypes.SET_REFRESH_ALERTS:
      return {
        ...state,
        shouldRefreshAlerts: action.value,
        allAlerts: [],
      };
    case AlertActionTypes.CLEAR_ALL_ALERTS:
      return {
        ...state,
        allAlerts: [],
      };
    case AlertActionTypes.REQUEST_ALL_ACTIVE_ALERTS:
    case AlertActionTypes.REQUEST_ALL_NEW_ALERTS:
    case AlertActionTypes.REQUEST_ALL_ALERTS_BY_COMMAND_STATUS:
      return {
        ...state,
        isLoadingAllAlerts: true,
      };
    case AlertActionTypes.RECEIVE_ALL_ACTIVE_ALERTS:
    case AlertActionTypes.RECEIVE_ALL_NEW_ALERTS:
    case AlertActionTypes.RECEIVE_ALL_ALERTS_BY_COMMAND_STATUS:
      return {
        ...state,
        allAlerts: action.allAlerts,
        isLoadingAllAlerts: false,
      };
    case AlertActionTypes.RECEIVE_ALERT_COUNTS:
      return {
        ...state,
        openAlertsCount: action.alertCounts.NewAlertsCount,
        inProgressAlertsCount: action.alertCounts.InProgressAlertsCount,
        closedAlertsCount: action.alertCounts.ClosedAlertsCount,
      };
    case AlertActionTypes.SEND_COMMAND_RESPONSE:
      return {
        ...state,
        shouldRefreshAlerts: false,
      };
    case AlertActionTypes.SENT_COMMAND_RESPONSE:
      return {
        ...state,
        shouldRefreshAlerts: true,
      };
    case AlertActionTypes.UNAUTHORIZED_REQUEST:
      return {
        ...state,
        unauthorizedRequest: true,
      };
    case AlertActionTypes.SET_START_DATE_TIME_FILTER:
      return {
        ...state,
        startDateTimeFilter: action.date,
      };
    case AlertActionTypes.SET_END_DATE_TIME_FILTER:
      return {
        ...state,
        endDateTimeFilter: action.date,
      };
    case AlertActionTypes.USE_CURRENT_DATE_TIME:
      return {
        ...state,
        useCurrentDateTimeFilter: action.value,
      };

    default:
      break;
  }

  return state;
};
